import React from 'react';
import classnames from 'classnames';
import { DragDropContext, DragStart, DragUpdate, Droppable, DropResult } from 'react-beautiful-dnd';
import { reorder } from '../../_events/_manage-event/_edit-website/website-form-body/helpers';
import { DragDropListItem } from './drag-drop-list-item';
import './drag-drop-list.scss';
import { ProjectCSPNonceDerictives } from '../../../constants/common.constants';
import { useDropPlaceholder } from './use-drop-placeholder';
import { useNumberedHeights } from './use-numbered-heights';
import { useIframeDragLock } from './use-iframe-drag-lock';

interface IDragDropListItem {
    id: string | number;
}

type DragHandle = 'icon' | 'item';

export interface IDragDropListProps<T> {
    data: T[];
    numbered?: boolean;
    dragHandleBy?: DragHandle;
    showDropPlaceholder?: boolean;
    renderItem: (props: T, index: number) => JSX.Element;
    onDragEnd: (data: T[]) => void;
}

const DragDropList = <T extends IDragDropListItem>({ numbered, dragHandleBy = 'icon', showDropPlaceholder = false, data, renderItem, onDragEnd }: IDragDropListProps<T>): JSX.Element => {
    const { placeholder, syncPlaceholder, clearPlaceholder } = useDropPlaceholder(showDropPlaceholder);
    const { onItemRendered, updateHeights, registerNumberRef } = useNumberedHeights(numbered);
    const { lockIframes, unlockIframes } = useIframeDragLock();

    const onDragStartHandler = (start: DragStart): void => {
        lockIframes();
        syncPlaceholder(start.draggableId, start.source.index, start.source.index);
    };

    const onDragUpdate = (update: DragUpdate): void => {
        updateHeights(update.source.index, update.destination?.index);
        syncPlaceholder(update.draggableId, update.source.index, update.destination?.index);
    };

    const onDragEndHandler = (result: DropResult): void => {
        unlockIframes();
        updateHeights();
        clearPlaceholder();
        if (result.destination) {
            const sortedData = reorder<T>(data, result.source.index, result.destination.index);
            onDragEnd(sortedData);
        }
    };

    const renderNumbers = (): JSX.Element => {
        return (
            <div className='drag-drop-number-list'>
                {data.map((item, index) => (
                    <span key={index} ref={registerNumberRef(index)} className='drag-drop-number-list__item'>
                        {index + 1}
                    </span>
                ))}
            </div>
        );
    };

    const listClassName = classnames('drag-drop-list', { 'drag-drop-list--whole-row': dragHandleBy === 'item' });

    return (
        <DragDropContext
            enableDefaultSensors={true}
            onDragStart={onDragStartHandler}
            onDragEnd={onDragEndHandler}
            onDragUpdate={onDragUpdate}
            nonce={ProjectCSPNonceDerictives.OEC_DRAG_DROP_STYLES}
        >
            <Droppable droppableId='droppable'>
                {droppableProps => {
                    return (
                        <div
                            className={listClassName}
                            {...droppableProps.droppableProps}
                            ref={droppableProps.innerRef}
                        >
                            {data.map((item, index) => {
                                return (
                                    <DragDropListItem
                                        key={index}
                                        index={index}
                                        dragHandleBy={dragHandleBy}
                                        renderItem={renderItem}
                                        listItem={item}
                                        onItemRendered={numbered && onItemRendered}
                                    />
                                );
                            })}
                            {droppableProps.placeholder}
                            {placeholder && (
                                <div
                                    className='drag-drop-list__drop-placeholder'
                                    style={{
                                        top: placeholder.top,
                                        left: placeholder.left,
                                        width: placeholder.width,
                                        height: placeholder.height,
                                    }}
                                />
                            )}
                            {numbered && renderNumbers()}
                        </div>
                    );
                }}
            </Droppable>
        </DragDropContext>
    );
};

export { DragDropList, IDragDropListItem, DragHandle };
